// © 2026 Camp Promethean, LLC. All Rights Reserved.
import { User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

interface UserChatAvatarProps {
  size?: "sm" | "md";
  className?: string;
}

const SIZE = { sm: "h-6 w-6 text-[10px]", md: "h-8 w-8 text-xs" } as const;

function initials(profile: { first_name?: string | null; last_name?: string | null } | null) {
  const letters = `${profile?.first_name?.[0] || ""}${profile?.last_name?.[0] || ""}`.toUpperCase();
  return letters || null;
}

export function UserChatAvatar({ size = "md", className }: UserChatAvatarProps) {
  const { profile } = useAuth();
  const name =
    profile?.display_name ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
    "You";
  const letters = initials(profile);

  return (
    <Avatar className={cn("shrink-0", SIZE[size], className)} aria-label={name}>
      {profile?.avatar_url && <AvatarImage src={profile.avatar_url} alt="" />}
      <AvatarFallback className="bg-primary/10 font-medium text-primary">
        {letters || <User className={size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} />}
      </AvatarFallback>
    </Avatar>
  );
}
